import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../../App';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Card, CardContent } from '../ui/card';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Checkbox } from '../ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../ui/alert-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { ChevronDown, ChevronRight, Users, RotateCcw, Code2, Radio, Trash2, Copy, Check, Zap, Undo2, CheckCircle2 } from 'lucide-react';

function UserMultiSelect({ users, selectedIds, onChange, disabled }) {
  const [open, setOpen] = useState(false);
  const toggle = (id) => { if (selectedIds.includes(id)) { onChange(selectedIds.filter((x) => x !== id)); } else { onChange([...selectedIds, id]); } };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" disabled={disabled} className="gap-1.5 text-xs h-8" data-testid="change-user-select-trigger">
          <Users className="w-3.5 h-3.5" />
          {selectedIds.length === 0 ? 'Select users' : `${selectedIds.length} user${selectedIds.length === 1 ? '' : 's'}`}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-2 max-h-72 overflow-y-auto" align="start">
        {users.length === 0 ? (<div className="text-xs text-gray-400 py-2 text-center">No users found</div>) : (
          users.map((u) => (
            <label key={u.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-50 cursor-pointer text-sm">
              <Checkbox checked={selectedIds.includes(u.id)} onCheckedChange={() => toggle(u.id)} data-testid={`change-user-option-${u.id}`} />
              <span className="truncate">{u.display_name || u.full_name || u.email}</span>
            </label>
          ))
        )}
      </PopoverContent>
    </Popover>
  );
}

function buildEmergentPrompt(change) {
  return `Apply this exact diff to the codebase. This is a pre-approved, already-reviewed change — apply it as a precise, minimal edit. Do not rebuild, redesign, or reinterpret the surrounding code, and do not modify anything outside what the diff touches.

Title: ${change.title}
${change.description ? `Description: ${change.description}\n` : ''}Diff:
${change.pending_diff}

After applying, confirm the affected file(s) still build/compile without errors. Do not touch any other file.

IMPORTANT — testing: do NOT call the testing agent, do NOT take screenshots, and do NOT run any automated testing of any kind for this change. The person applying this will test it manually themselves.`;
}

function ChangeRow({ change, users, onUpdate, onRevert, onApplyDiff, onDelete, onApplyDirect, onRevertDirect }) {
  const [confirmLiveOpen, setConfirmLiveOpen] = useState(false);
  const [diffOpen, setDiffOpen] = useState(false);
  const [confirmDirectOpen, setConfirmDirectOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [applyingDirect, setApplyingDirect] = useState(false);
  const [copied, setCopied] = useState(false);

  const hasDirectApply = Array.isArray(change.file_changes) && change.file_changes.length > 0;
  const canApplyDirect = hasDirectApply && change.pending_diff; // pending_diff cleared once applied (direct or otherwise)

  const handleAdminToggle = async (val) => { setSaving(true); try { await onUpdate(change.id, { enabled_admin: val, ...(val ? {} : { enabled_user_ids: [] }) }); } finally { setSaving(false); } };
  const handleUsersChange = async (ids) => { await onUpdate(change.id, { enabled_user_ids: ids }); };
  const handleLiveToggle = (val) => { if (val) { setConfirmLiveOpen(true); } else { onUpdate(change.id, { is_live: false }); } };
  const confirmGoLive = async () => { setConfirmLiveOpen(false); setSaving(true); try { await onUpdate(change.id, { is_live: true }); toast.success(`"${change.title}" is now live for everyone`); } finally { setSaving(false); } };
  const handleCopyPrompt = async () => { try { await navigator.clipboard.writeText(buildEmergentPrompt(change)); setCopied(true); toast.success('Prompt copied — paste it straight into Emergent'); setTimeout(() => setCopied(false), 2000); } catch { toast.error('Failed to copy — your browser may be blocking clipboard access'); } };

  const handleApplyDirect = async () => {
    setConfirmDirectOpen(false);
    setApplyingDirect(true);
    try {
      await onApplyDirect(change.id);
    } finally {
      setApplyingDirect(false);
      setDiffOpen(false);
    }
  };

  const lastChangedLabel = change.last_toggled_at ? `Last changed ${new Date(change.last_toggled_at).toLocaleString()}` : null;

  return (
    <Card className="border" data-testid={`change-row-${change.id}`}>
      <CardContent className="py-3 px-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <div className="font-medium text-sm text-gray-900">{change.title}</div>
            {change.description && (<div className="text-xs text-gray-500 mt-0.5">{change.description}</div>)}
            {lastChangedLabel && (<div className="text-[11px] text-gray-400 mt-1">{lastChangedLabel}</div>)}
            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
              {change.is_live && (
                <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full" style={{ backgroundColor: '#DCFCE7', color: '#166534' }}>
                  <Radio className="w-3 h-3" /> Live for everyone
                </span>
              )}
              {hasDirectApply && (
                <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full" style={{ backgroundColor: '#EDE9FE', color: '#6D28D9' }} title="This proposal has structured file content and can be written directly to the preview site">
                  <Zap className="w-3 h-3" /> Direct-apply ready
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex flex-col items-center gap-1">
              <Switch checked={change.enabled_admin} onCheckedChange={handleAdminToggle} disabled={saving} data-testid={`change-admin-toggle-${change.id}`} />
              <span className="text-[10px] text-gray-500">Super Admin</span>
            </div>
            <div className="flex flex-col items-center gap-1">
              <UserMultiSelect users={users} selectedIds={change.enabled_user_ids || []} onChange={handleUsersChange} disabled={!change.enabled_admin || saving} />
              <span className="text-[10px] text-gray-500">Specific Users</span>
            </div>
            <div className="flex flex-col items-center gap-1">
              <Switch checked={change.is_live} onCheckedChange={handleLiveToggle} disabled={saving} data-testid={`change-live-toggle-${change.id}`} className="data-[state=checked]:bg-green-600" />
              <span className="text-[10px] text-gray-500">Live</span>
            </div>
            <div className="flex items-center gap-1">
              {change.pending_diff && (<Button variant="outline" size="sm" className="h-8 gap-1 text-xs" onClick={() => setDiffOpen(true)} data-testid={`change-apply-diff-btn-${change.id}`}><Code2 className="w-3.5 h-3.5" /> Apply diff</Button>)}
              {!change.pending_diff && hasDirectApply && (
                <Button variant="outline" size="sm" className="h-8 gap-1 text-xs" onClick={() => onRevertDirect(change.id)} title="Restore the files this change wrote back to what they were before" data-testid={`change-revert-direct-btn-${change.id}`}>
                  <Undo2 className="w-3.5 h-3.5" /> Revert files
                </Button>
              )}
              <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-400 hover:text-red-600" onClick={() => onRevert(change.id)} title="Revert visibility — turns all toggles off" data-testid={`change-revert-btn-${change.id}`}>
                <RotateCcw className="w-3.5 h-3.5" />
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-400 hover:text-red-600" onClick={() => onDelete(change.id, change.title)} title="Delete this entry permanently" data-testid={`change-delete-btn-${change.id}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        </div>
      </CardContent>

      <AlertDialog open={confirmLiveOpen} onOpenChange={setConfirmLiveOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Go live for everyone?</AlertDialogTitle>
            <AlertDialogDescription>"{change.title}" will become visible to all users immediately, overriding the Super Admin and specific-user toggles. This can be reverted at any time.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmGoLive} className="bg-green-600 hover:bg-green-700">Go Live</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmDirectOpen} onOpenChange={setConfirmDirectOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Write this directly to the connected preview site?</AlertDialogTitle>
            <AlertDialogDescription>
              This writes real files to whichever deployment your MCP connector currently points
              at (your preview/test site). The exact previous content of every file touched is
              saved first, so "Revert files" can undo it afterward. This does not change who can
              see it — that's still controlled by the toggles above.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleApplyDirect} className="bg-purple-600 hover:bg-purple-700">Write Files Now</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={diffOpen} onOpenChange={setDiffOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>Apply — {change.title}</DialogTitle></DialogHeader>
          <pre className="text-xs bg-gray-900 text-gray-100 rounded-lg p-4 overflow-auto max-h-96 whitespace-pre-wrap">{change.pending_diff}</pre>
          {hasDirectApply ? (
            <p className="text-xs text-gray-500">
              This proposal has structured file content for {change.file_changes.length} file(s) — you can
              write it straight to your connected preview site now, or still copy the prompt for Emergent
              if you'd rather apply it that way.
            </p>
          ) : (
            <p className="text-xs text-gray-500">
              Copy the ready-made prompt below and paste it straight into Emergent — it wraps the
              diff with instructions telling Emergent to apply it exactly, as a precise edit rather
              than a rebuild, and to skip automated testing since you'll test it yourself.
            </p>
          )}
          <DialogFooter className="flex-col sm:flex-row sm:justify-between sm:items-center gap-2 w-full">
            <Button variant="outline" onClick={() => setDiffOpen(false)} className="sm:mr-auto">Cancel</Button>
            <div className="flex flex-col sm:flex-row gap-2 flex-wrap sm:justify-end">
              <Button variant="outline" onClick={handleCopyPrompt} className="gap-1.5" data-testid={`change-copy-prompt-btn-${change.id}`}>
                {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                {copied ? 'Copied' : 'Copy Prompt for Emergent'}
              </Button>
              {hasDirectApply && (
                <Button
                  onClick={() => setConfirmDirectOpen(true)}
                  disabled={applyingDirect}
                  className="gap-1.5 bg-purple-600 hover:bg-purple-700"
                  data-testid={`change-apply-direct-btn-${change.id}`}
                >
                  <Zap className="w-3.5 h-3.5" /> {applyingDirect ? 'Writing...' : 'Apply Directly'}
                </Button>
              )}
              <Button onClick={async () => { await onApplyDiff(change.id); setDiffOpen(false); }} data-testid={`change-confirm-apply-diff-${change.id}`}>
                Mark as Applied
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// Collapsed row shown inside the "Completed" section — title + Live dot only,
// until clicked, then expands into the exact same full ChangeRow (same
// toggles, same behavior) so nothing about editing a completed change changes.
function CompletedRow({ change, users, onUpdate, onRevert, onApplyDiff, onDelete, onApplyDirect, onRevertDirect }) {
  const [expanded, setExpanded] = useState(false);

  if (expanded) {
    return (
      <div className="space-y-1">
        <button
          onClick={() => setExpanded(false)}
          className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600"
          data-testid={`completed-row-collapse-${change.id}`}
        >
          <ChevronDown className="w-3.5 h-3.5" /> Collapse
        </button>
        <ChangeRow
          change={change}
          users={users}
          onUpdate={onUpdate}
          onRevert={onRevert}
          onApplyDiff={onApplyDiff}
          onDelete={onDelete}
          onApplyDirect={onApplyDirect}
          onRevertDirect={onRevertDirect}
        />
      </div>
    );
  }

  return (
    <button
      onClick={() => setExpanded(true)}
      className="w-full flex items-center gap-2 px-4 py-2.5 rounded-lg border bg-gray-50 hover:bg-gray-100 transition-colors text-left"
      data-testid={`completed-row-${change.id}`}
    >
      <CheckCircle2 className="w-3.5 h-3.5 text-green-600 shrink-0" />
      <span className="flex-1 text-sm text-gray-700 truncate">{change.title}</span>
      <ChevronRight className="w-3.5 h-3.5 text-gray-400 shrink-0" />
    </button>
  );
}

function GroupedChanges({ items, users, collapsedGroups, onToggleGroup, onUpdate, onRevert, onApplyDiff, onDelete, onApplyDirect, onRevertDirect, completedCollapsed, onToggleCompleted }) {
  const activeItems = items.filter((c) => !c.is_live);
  const completedItems = items.filter((c) => c.is_live);

  const grouped = activeItems.reduce((acc, c) => { const key = c.group_header || 'General'; (acc[key] = acc[key] || []).push(c); return acc; }, {});

  return (
    <div className="space-y-6">
      {Object.entries(grouped).map(([group, groupItems]) => (
        <div key={group} className="space-y-2">
          <button onClick={() => onToggleGroup(group)} className="flex items-center gap-1.5 text-sm font-semibold text-gray-700 hover:text-gray-900" data-testid={`change-group-header-${group}`}>
            {collapsedGroups[group] ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            {group}
            <span className="text-xs font-normal text-gray-400">({groupItems.length})</span>
          </button>
          {!collapsedGroups[group] && (
            <div className="space-y-2 pl-1">
              {groupItems.map((change) => (
                <ChangeRow
                  key={change.id}
                  change={change}
                  users={users}
                  onUpdate={onUpdate}
                  onRevert={onRevert}
                  onApplyDiff={onApplyDiff}
                  onDelete={onDelete}
                  onApplyDirect={onApplyDirect}
                  onRevertDirect={onRevertDirect}
                />
              ))}
            </div>
          )}
        </div>
      ))}

      {completedItems.length > 0 && (
        <div className="space-y-2 pt-2 border-t">
          <button onClick={onToggleCompleted} className="flex items-center gap-1.5 text-sm font-semibold text-green-700 hover:text-green-800" data-testid="change-completed-header">
            {completedCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            <CheckCircle2 className="w-4 h-4" />
            Completed
            <span className="text-xs font-normal text-gray-400">({completedItems.length})</span>
          </button>
          {!completedCollapsed && (
            <div className="space-y-1.5 pl-1">
              {completedItems.map((change) => (
                <CompletedRow
                  key={change.id}
                  change={change}
                  users={users}
                  onUpdate={onUpdate}
                  onRevert={onRevert}
                  onApplyDiff={onApplyDiff}
                  onDelete={onDelete}
                  onApplyDirect={onApplyDirect}
                  onRevertDirect={onRevertDirect}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function ManageChangesSettings({ user }) {
  const [changes, setChanges] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [collapsedCompleted, setCollapsedCompleted] = useState({});
  const [activeType, setActiveType] = useState(null);

  const isSuperAdmin = user?.role === 'super_admin';

  const load = async () => {
    try {
      const [changesRes, usersRes] = await Promise.all([axios.get(`${API}/code-changes`), axios.get(`${API}/users`)]);
      const loadedChanges = changesRes.data || [];
      setChanges(loadedChanges);
      setUsers(usersRes.data || []);
      setActiveType((prev) => { if (prev) return prev; const firstType = loadedChanges[0]?.type || 'General'; return firstType; });
    } catch { toast.error('Failed to load Manage Changes'); } finally { setLoading(false); }
  };

  useEffect(() => { if (isSuperAdmin) load(); }, [isSuperAdmin]); // eslint-disable-line

  const handleUpdate = async (id, fields) => { try { const res = await axios.put(`${API}/code-changes/${id}`, fields); setChanges((prev) => prev.map((c) => (c.id === id ? res.data : c))); } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to update change'); } };
  const handleRevert = async (id) => { if (!window.confirm('Revert visibility? All toggles will be turned off.')) return; try { const res = await axios.post(`${API}/code-changes/${id}/revert`); setChanges((prev) => prev.map((c) => (c.id === id ? res.data : c))); toast.success('Visibility reverted'); } catch { toast.error('Failed to revert'); } };
  const handleApplyDiff = async (id) => { try { const res = await axios.post(`${API}/code-changes/${id}/apply-diff`); setChanges((prev) => prev.map((c) => (c.id === id ? res.data : c))); toast.success('Diff marked as applied'); } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to apply diff'); } };
  const handleDelete = async (id, title) => { if (!window.confirm(`Permanently delete "${title}"? This cannot be undone.`)) return; try { await axios.delete(`${API}/code-changes/${id}`); setChanges((prev) => prev.filter((c) => c.id !== id)); toast.success('Change deleted'); } catch { toast.error('Failed to delete change'); } };

  const handleApplyDirect = async (id) => {
    try {
      const res = await axios.post(`${API}/code-changes/${id}/apply-direct`);
      setChanges((prev) => prev.map((c) => (c.id === id ? res.data.change : c)));
      toast.success(`Written to preview: ${res.data.files_written.join(', ')}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to apply directly');
    }
  };

  const handleRevertDirect = async (id) => {
    if (!window.confirm('Revert these files back to what they were before this change was applied?')) return;
    try {
      const res = await axios.post(`${API}/code-changes/${id}/revert-direct`);
      toast.success(`Reverted: ${res.data.restored.map((r) => r.path).join(', ')}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to revert');
    }
  };

  const toggleGroup = (group) => { setCollapsedGroups((prev) => ({ ...prev, [group]: !prev[group] })); };
  const toggleCompleted = (type) => { setCollapsedCompleted((prev) => ({ ...prev, [type]: prev[type] === false ? true : false })); };

  if (!isSuperAdmin) { return <div className="text-sm text-gray-500 py-8 text-center">Super admin access required.</div>; }
  if (loading) { return <div className="text-sm text-gray-500 py-12 text-center">Loading changes...</div>; }

  const types = [];
  changes.forEach((c) => { const t = c.type || 'General'; if (!types.includes(t)) types.push(t); });
  const byType = changes.reduce((acc, c) => { const key = c.type || 'General'; (acc[key] = acc[key] || []).push(c); return acc; }, {});

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Manage Changes</h2>
        <p className="text-sm text-gray-500 mt-1">Stage and roll out AI-proposed or manual code changes safely. Nothing reaches real users until a toggle here says so.</p>
      </div>
      {types.length === 0 ? (
        <div className="text-center py-12 text-gray-400 text-sm border-2 border-dashed rounded-lg">No changes yet.</div>
      ) : (
        <Tabs value={activeType || types[0]} onValueChange={setActiveType}>
          <TabsList className="flex-wrap h-auto">
            {types.map((t) => (<TabsTrigger key={t} value={t} data-testid={`change-type-tab-${t}`}>{t}<span className="ml-1.5 text-xs text-gray-400">({byType[t].length})</span></TabsTrigger>))}
          </TabsList>
          {types.map((t) => (
            <TabsContent key={t} value={t} className="pt-4">
              <GroupedChanges
                items={byType[t]}
                users={users}
                collapsedGroups={collapsedGroups}
                onToggleGroup={toggleGroup}
                onUpdate={handleUpdate}
                onRevert={handleRevert}
                onApplyDiff={handleApplyDiff}
                onDelete={handleDelete}
                onApplyDirect={handleApplyDirect}
                onRevertDirect={handleRevertDirect}
                completedCollapsed={collapsedCompleted[t] !== false}
                onToggleCompleted={() => toggleCompleted(t)}
              />
            </TabsContent>
          ))}
        </Tabs>
      )}
    </div>
  );
}
